import FadeUp from "./FadeUp";
import { Handshake, Scale, Target, Gauge } from "lucide-react";

const principles = [
    {
        id: "performance-deals",
        icon: Handshake,
        title: "Performance-based deal structures",
        body: "We push for hybrid deals and performance-based partnerships wherever possible to reduce upfront risk and align creator incentives with business outcomes.",
        tags: ["Hybrid deals", "Performance-based partnerships"],
    },
    {
        id: "negotiation",
        icon: Scale,
        title: "Hard negotiation",
        body: "We negotiate creator fees, deliverables, usage rights, and deal terms carefully to make sure every dollar has a clear purpose.",
        tags: ["Fees", "Deliverables", "Usage rights", "Deal terms"],
    },
    {
        id: "pipeline",
        icon: Target,
        title: "Pipeline over vanity metrics",
        body: "We optimize campaigns for trials, demos, qualified leads, and sales instead of focusing on views, engagement, or EMV.",
        tags: ["Trials", "Demos", "Qualified leads", "Sales"],
        struck: ["Views", "Engagement", "EMV"],
    },
    {
        id: "scoring",
        icon: Gauge,
        title: "Proprietary creator scoring",
        body: "We evaluate creators using relevant data points, including audience fit, engagement quality, content relevance, historical performance where available, and expected business outcomes.",
        extra: "We prioritize creators based on their potential to reach the right audience and drive meaningful actions, not just their follower or subscriber counts.",
        tags: ["Audience fit", "Engagement quality", "Content relevance", "Historical performance", "Expected outcomes"],
    },
];

const Tag = ({ children, struck = false }) => (
    <span
        className={`inline-flex items-center rounded-full px-2.5 h-7 text-[11.5px] font-semibold whitespace-nowrap ${
            struck
                ? "bg-transparent border border-dashed border-black/15 text-gray-400 line-through"
                : "bg-[#FAFAFA] border border-black/[0.06] text-[#0A0A0A]"
        }`}
    >
        {children}
    </span>
);

export const HowWeDoIt = () => (
    <section
        id="how-we-do-it"
        data-testid="how-we-do-it-section"
        className="py-24 md:py-32 bg-[#FAFAFA] border-y border-black/[0.06]"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="how-we-do-it-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.4] md:leading-[1.4] lg:leading-[1.4] text-[#0A0A0A] text-balance"
                    >
                        Every dollar well spent.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-[1.75] max-w-2xl">
                        Four principles that decide how your creator budget gets spent.
                    </p>
                </FadeUp>
            </div>

            <div className="mt-14 grid md:grid-cols-2 gap-4 md:gap-5">
                {principles.map((p, i) => (
                    <FadeUp key={p.id} delay={0.05 + (i % 2) * 0.05}>
                        <div
                            data-testid={`how-we-do-it-card-${p.id}`}
                            className="h-full rounded-2xl border border-black/[0.08] bg-white p-6 md:p-7 hover:border-black/20 transition-colors flex flex-col"
                        >
                            <div className="flex items-start gap-4">
                                <div className="flex-none inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#0A0A0A]">
                                    <p.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-gray-500">
                                        0{i + 1}
                                    </div>
                                    <h3 className="mt-1 font-display font-bold text-lg md:text-xl tracking-tight text-[#0A0A0A] leading-[1.4]">
                                        {p.title}
                                    </h3>
                                </div>
                            </div>
                            <p className="mt-5 text-[14.5px] text-gray-600 leading-[1.75]">
                                {p.body}
                            </p>
                            {p.extra && (
                                <p className="mt-3 text-[14.5px] text-gray-600 leading-[1.75]">
                                    {p.extra}
                                </p>
                            )}
                            <div className="mt-6 pt-5 border-t border-black/[0.06] flex flex-wrap gap-1.5">
                                {p.tags.map((t) => (
                                    <Tag key={t}>{t}</Tag>
                                ))}
                                {p.struck?.map((t) => (
                                    <Tag key={t} struck>
                                        {t}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

export default HowWeDoIt;
